import type { Payment, Restrictions } from 'payments-lib-types';
import { filter, is, pluck } from 'ramda';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import {
  clearBonusCode,
  type DepositBonusCode,
  setBonusCode,
  type User,
  updateBonusSettings,
} from '@/api/auth';
import type { BonusMinDepV2, CMSBonus } from '@/api/cms/bonuses';
import type { Currency } from '@/api/common';
import { formatTextUppercase } from '@/helpers/common';
import { CLIENT_ENVS } from '@/helpers/constants';
import {
  amountWithCurrencySymbol,
  DEFAULT_CURRENCIES,
  getCurrencyName,
  MAP_COUNTRY_TO_CURRENCY,
} from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import { fetchPaymentMethods } from '@/helpers/payments';
import { unmaskAmount } from '@/helpers/validators';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import type { ICMSPromocode } from '@/modules/common/hooks/useCMSPromocodes';
import {
  BECOME_VIP_AMOUNTS,
  DEFAULT_AMOUNTS,
  DepositMode,
  DepositStep,
  getMinBonusDepByBonus,
  getMinBonusDepByPromocode,
  getMinDepositAmount,
} from '@/modules/deposit';
import {
  sendDepositPaymentMethodsFailedLoading,
  sendDepositPromoCodeActivateEvent,
} from '@/modules/googleAnalytics';
import type { CommonStoreType } from '../commonStore/types';
import { createSelectors } from '../helpers';

export interface PaymentMethod extends Payment.Method {
  internalId: string;
  savedProfileDetails?: Payment.SavedProfile;
  isPrevUsed?: boolean;
}

export interface DepositBonusCodeError {
  depositBonusCode?: string[];
  depositCurrency?: string[];
  depositSumInCents?: string[];
  errorCode?: string;
  message?: string;
}

export interface DepositStorageState {
  step: DepositStep;
  mode: DepositMode;
  currentAmount: number | string;
  currentAmountWithCurrency: number | string;
  originalAmount: number | string;
  activeThemeMode: ThemeMode | undefined;
  defaultAmountRestrictions: Restrictions | undefined;
  currentPaymentMethod: PaymentMethod | null;
  pendingBonuses: boolean;
  paymentMethods: PaymentMethod[];
  pendingPaymentMethods: boolean;
  previouslyUsedPaymentMethods: PaymentMethod[];
  pendingBonusCode: boolean;
  selectedBonus: CMSBonus | null;
  initialBonusTab: number | null;
  setInitialBonusTab: (tab: number | null) => void;
  setCurrentAmount: (
    user: User | undefined,
    currencies: Currency[],
    amount: number | string
  ) => void;
  setOriginalAmount: (amount: number | string) => void;
  fetchPaymentMethods: (currency?: string) => void;
  handleRemoveBonusCode: (params: {
    user?: User;
    updateUser: CommonStoreType['updateUser'];
    refreshUser: CommonStoreType['refreshUser'];
    currencies?: Currency[];
    isVIP?: boolean;
  }) => Promise<void>;
  handleSetBonusCode: (params: {
    user?: User;
    updateUser: CommonStoreType['updateUser'];
    refreshUser: CommonStoreType['refreshUser'];
    values: DepositBonusCode;
    fromForm?: boolean;
    bonuses?: CMSBonus[];
    bonusMinDeps?: BonusMinDepV2[];
    increasedPrevDepValue?: number;
    currencies?: Currency[];
    isVIP?: boolean;
    promocodes?: ICMSPromocode[];
  }) => Promise<void>;
  handleChangeReceiveBonuses: (
    user: User | undefined,
    updateUser: CommonStoreType['updateUser'],
    refreshUser: CommonStoreType['refreshUser']
  ) => void;
  setDefaultAmountRestrictions: (
    user: User | undefined,
    countryCode: string
  ) => void;
}

let fetchMethodsRequestId = 0;

const useDepositStoreBase = create<DepositStorageState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      step: DepositStep.SELECT_WALLET,
      mode: DepositMode.SLOTS,
      currentAmount: '',
      currentAmountWithCurrency: '',
      originalAmount: '',
      activeThemeMode: ThemeMode.Light,
      defaultAmountRestrictions: undefined,
      currentPaymentMethod: null,
      pendingBonuses: false,
      paymentMethods: [],
      pendingPaymentMethods: true,
      previouslyUsedPaymentMethods: [],
      pendingBonusCode: false,
      selectedBonus: null,
      initialBonusTab: null,
      setInitialBonusTab: (tab) => set({ initialBonusTab: tab }),
      setCurrentAmount: (user, currencies, currentAmount) => {
        const currencyConversion = (amount: number, currency: string) =>
          getCurrencyCentsAndCode(amount, currency, currencies).currency;

        const currentAmountWithCurrency = (() => {
          if (!user?.currency) return 0;
          const amount = currentAmount ? unmaskAmount(currentAmount) : 0;
          const currency = currencyConversion(amount, user.currency);

          return amountWithCurrencySymbol(
            getCurrencyName(currency),
            currentAmount
          );
        })();
        set({ currentAmount, currentAmountWithCurrency });
      },
      setOriginalAmount: (originalAmount: number | string) => {
        set({ originalAmount });
      },
      fetchPaymentMethods: async (currency?: string) => {
        const requestId = ++fetchMethodsRequestId;
        set({ pendingPaymentMethods: true });

        if (!currency) {
          sendDepositPaymentMethodsFailedLoading({ error: 'no_user_currency' });
          set({ pendingPaymentMethods: false });
          return;
        }

        try {
          const { data } = await fetchPaymentMethods({ currency });

          if (requestId !== fetchMethodsRequestId) return;

          const paymentMethods = data.map((paymentMethod) => ({
            ...paymentMethod,
            internalId: paymentMethod.id,
          }));
          set({ paymentMethods, pendingPaymentMethods: false });
        } catch (error) {
          if (requestId !== fetchMethodsRequestId) return;
          console.log(error);
          set({ pendingPaymentMethods: false });
        }
      },
      handleRemoveBonusCode: async ({
        user,
        updateUser,
        refreshUser,
        currencies = [],
        isVIP = false,
      }) => {
        if (get().pendingBonusCode) return;

        const setCurrentAmount = get().setCurrentAmount;
        const originalAmount = Number(get().originalAmount);
        const prevBonusCode = user?.depositBonusCode;

        set({ pendingBonusCode: true });

        if (user) updateUser({ depositBonusCode: undefined });

        const { error } = await clearBonusCode();

        if (error) {
          if (user) updateUser({ depositBonusCode: prevBonusCode });
          set({ pendingBonusCode: false });
          return;
        }

        refreshUser();

        if (user) {
          const getAmount = () => {
            const base = isVIP
              ? BECOME_VIP_AMOUNTS[user.currency]
              : DEFAULT_AMOUNTS[user.currency];
            return originalAmount > 0 ? originalAmount : base;
          };

          setCurrentAmount(user, currencies, getAmount());
        }

        set({
          selectedBonus: null,
          pendingBonusCode: false,
        });
      },
      handleSetBonusCode: async ({
        user,
        updateUser,
        refreshUser,
        values,
        fromForm = false,
        bonuses = [],
        bonusMinDeps,
        increasedPrevDepValue = 0,
        currencies = DEFAULT_CURRENCIES,
        isVIP = false,
        promocodes = [],
      }) => {
        if (get().pendingBonusCode) return;

        const formatCode = formatTextUppercase(values.depositBonusCode.trim());
        const mode = get().mode;
        const originalAmount = get().originalAmount;
        const setCurrentAmount = get().setCurrentAmount;
        const prevBonusCode = user?.depositBonusCode;

        set({ pendingBonusCode: true });

        if (user) updateUser({ depositBonusCode: formatCode });

        const { error } = await setBonusCode({
          depositBonusCode: formatCode,
          depositCurrency: values.depositCurrency,
          depositSumInCents: values.depositSumInCents,
        });

        if (fromForm) {
          sendDepositPromoCodeActivateEvent({
            dep_modal_type: mode,
            code: values.depositBonusCode,
            code_sent_to_back: formatCode,
            status: 'success',
            error_data: error && JSON.stringify(error),
          });
        }

        if (error) {
          if (user) updateUser({ depositBonusCode: prevBonusCode });
          set({ pendingBonusCode: false });
          throw error;
        }

        refreshUser();

        if (user) {
          const minDepByBonus = getMinBonusDepByBonus(bonuses, {
            ...user,
            depositBonusCode: formatCode,
          });
          const minDepByPromocode = getMinBonusDepByPromocode(promocodes, {
            ...user,
            depositBonusCode: formatCode,
          });
          const minDepByProgressiveBonus = bonusMinDeps
            ? getMinDepositAmount(bonusMinDeps, user.currency)
            : 0;
          const min =
            minDepByBonus || minDepByPromocode || minDepByProgressiveBonus;

          if (!min) {
            set({ pendingBonusCode: false });
            return;
          }

          const numericOriginal =
            (Number.isNaN(originalAmount) ? 0 : Number(originalAmount)) ||
            DEFAULT_AMOUNTS[user.currency] ||
            0;

          const amount = isVIP
            ? BECOME_VIP_AMOUNTS[user.currency]
            : Math.max(min, numericOriginal, increasedPrevDepValue);

          setCurrentAmount(user, currencies, amount);
          set({ pendingBonusCode: false });
        }

        set({ pendingBonusCode: false });
      },
      handleChangeReceiveBonuses: async (user, updateUser, refreshUser) => {
        const previousValue = Boolean(user?.canIssueBonuses);
        const newValue = !previousValue;

        updateUser({ canIssueBonuses: newValue });

        const { error } = await updateBonusSettings(newValue);

        if (error) {
          updateUser({ canIssueBonuses: previousValue });
          return;
        }

        refreshUser();
      },
      setDefaultAmountRestrictions: (user, countryCode) => {
        const { paymentMethods } = get();

        if (!(paymentMethods && countryCode)) return;

        const restrictionsList = paymentMethods.map(
          ({ termsOfService }) => termsOfService.restrictions
        );
        const minAmountValueList: number[] = filter(
          is(Number),
          pluck('minAmountValue', restrictionsList)
        );
        const maxAmountValueList: number[] = filter(
          is(Number),
          pluck('maxAmountValue', restrictionsList)
        );

        const defaultAmountRestrictions = {
          minAmountValue: minAmountValueList?.length
            ? Math.min(...minAmountValueList)
            : null,
          maxAmountValue: maxAmountValueList?.length
            ? Math.max(...maxAmountValueList)
            : null,
          amountCurrencyCode:
            user?.currency || MAP_COUNTRY_TO_CURRENCY[countryCode],
        };
        set({ defaultAmountRestrictions });
      },
    })),
    { name: 'useDepositStore', enabled: CLIENT_ENVS.APP_ENV === 'development' }
  )
);

export const useDepositStore = createSelectors(useDepositStoreBase);
